import { cn } from '@/lib/cn';

/**
 * Toggle — accessible on/off switch with the OllieReads brand colours.
 *
 * Implemented with a `<button role="switch">` rather than Radix Switch
 * (not in the bundle) so the component stays zero-dependency. Disabled
 * state dims the pill and freezes the thumb — used while a network
 * write is in flight so the UI can't get into a rapid double-tap.
 */
export interface ToggleProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  /** Accessible name. Required so screen readers don't see a bare "switch". */
  label: string;
  className?: string;
}

export function Toggle({ checked, onChange, disabled, label, className }: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-orange-500/40',
        checked ? 'bg-or-orange-500' : 'bg-or-slate-200',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        className,
      )}
    >
      <span
        className={cn(
          'inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition',
          checked ? 'translate-x-5' : 'translate-x-0.5',
        )}
        aria-hidden
      />
    </button>
  );
}
